/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineComponent, onMounted, onUnmounted } from "vue";
import {
  getProfileService,
  regenerateTokenService,
} from "@/services/apis/auth.service";
import { useStore } from "vuex";
import {
  accessToken,
  createDynamicApi,
  queryParams,
  setCurrentPath,
} from "@/services/utils/helper.util";
import { globals } from "@/main";
import { listMasterDataService } from "@/services/apis/master.data.service";
import { getMediaFolderService } from "@/services/apis/media.service";
import * as definePage from "@/router/modules/view.apizip.module";
import { useRouter } from "vue-router";
import { localOffset } from "@/dto/local.key.dto";

type PermissionCode = string;

type Permission = {
  adminRouteUrl: string;
  adminRouteName: string;
  query: string;
  params: object;
  children?: Child[];
};

type Child = {
  adminRouteUrl: string;
  adminRouteName: string;
  query: string;
  params: object;
  children?: GrandChild[];
};

type GrandChild = {
  adminRouteUrl: string;
  adminRouteName: string;
  query: string;
  params: object;
};

export default defineComponent({
  name: "App",

  setup() {
    const router = useRouter();

    // sample lifecycle
    onMounted(async () => {
      if (accessToken?.value) {
        await getMasterData();
        await getProfile();
      }

      document.title = "Processing...";
      document.body.addEventListener("mouseover", handleModalMouseOver);
      document.addEventListener("keydown", handleKeyPress);
    });

    onUnmounted(() => {
      document.body.removeEventListener("mouseover", handleModalMouseOver);
      document.removeEventListener("keydown", handleKeyPress);
    });

    // data modified
    const store = useStore();
    const isLoading = true;
    let isLocalSetting = false;
    let isCalculateExpression = false;
    let idEscape = false;
    let newModalContainers = document.querySelectorAll(".modal-container");
    let serverError = false;

    // api handlers
    const getProfile = async (): Promise<void> => {
      const params = queryParams({
        retrievePermission: 1,
      });
      store.commit("storeProcessLoad", true);
      localStorage.setItem(localOffset, String(0));
      globals.$helper.setStateFilter("");

      try {
        const response = await getProfileService(params);
        store.commit("storeProcessLoad", false);

        if (response?.statusCode === 500) {
          document.title = "DSI Admin - Severe Error 500";
          serverError = true;
          return;
        }

        // Check if token is expired
        if (response?.expired) {
          const refresh = await regenerateTokenService();
          if (refresh) {
            return await getProfile();
          }
        }

        document.title = "DSI Admin";
        globals.$store.commit("storeProfile", response?.data);

        // Set permissions
        const permissions: PermissionCode[] = extractPermissions(
          response?.data?.permissions
        );
        globals.$store.commit("storePermissions", permissions || []);

        // Set admin routes
        const routes: {
          query: string;
          params: object;
          path: string;
          name: string;
          component: any;
        }[] = [];

        const stockHistoryRoute = [
          {
            id: 10401,
            level: 5,
            title: "Stock Transaction History",
            titleForeign: "ស្តុក",
            code: "STK01",
            icon: "",
            isDropdown: null,
            isAdminVisible: true,
            adminRouteUrl: "/stock/transaction-history",
            adminRouteName: "viewStockTransactionHistory",
            apiMethod: "",
            apiUrl: "",
            children: [],
          },
          {
            id: 10401,
            level: 6,
            title: "Stock Transfer History",
            titleForeign: "ស្តុក",
            code: "STK01",
            icon: "",
            isDropdown: null,
            isAdminVisible: true,
            adminRouteUrl: "/stock/transfer-history",
            adminRouteName: "viewStockTransferHistory",
            apiMethod: "",
            apiUrl: "",
            children: [],
          },
          {
            id: 10401,
            level: 7,
            title: "Stock Reconcile History",
            titleForeign: "ស្តុក",
            code: "STK01",
            icon: "",
            isDropdown: null,
            isAdminVisible: true,
            adminRouteUrl: "/stock/reconcile-history",
            adminRouteName: "viewStockReconcileHistory",
            apiMethod: "",
            apiUrl: "",
            children: [],
          },
        ];

        (response?.data?.permissions || []).forEach(
          (permission: Permission) => {
            // Custom Route
            if (permission?.adminRouteUrl === "/stock") {
              stockHistoryRoute.forEach((customRoute) => {
                routes.push({
                  path: customRoute?.adminRouteUrl,
                  name: customRoute?.adminRouteName,
                  query: "",
                  params: {},
                  component: (definePage as Record<string, any>)[
                    customRoute?.adminRouteName || "viewSoon"
                  ],
                });
              });
            }

            routes.push({
              path: permission?.adminRouteUrl,
              name: permission?.adminRouteName,
              query: permission?.query || "",
              params: permission?.params || {},
              component: (definePage as Record<string, any>)[
                permission?.adminRouteName || "viewSoon"
              ],
            });

            (permission.children || []).forEach((child: Child) => {
              routes.push({
                path: child?.adminRouteUrl,
                name: child?.adminRouteName,
                query: child?.query || "",
                params: child?.params || {},
                component: (definePage as Record<string, any>)[
                  child?.adminRouteName || "viewSoon"
                ],
              });

              (child.children || []).forEach((grandChild: GrandChild) => {
                routes.push({
                  path: grandChild?.adminRouteUrl,
                  name: grandChild?.adminRouteName,
                  query: grandChild?.query || "",
                  params: grandChild?.params || {},
                  component: (definePage as Record<string, any>)[
                    grandChild?.adminRouteName || "viewSoon"
                  ],
                });
              });
            });
          }
        );

        const currentRoute = globals.$route;

        routes.forEach((route) => {
          const routeConfig = {
            path: route?.path,
            name: route?.name,
            query: route?.query || "",
            params: route?.params || "",
            component:
              route.component !== undefined
                ? route.component
                : definePage["viewSoon"],
          };

          router.addRoute(routeConfig);
        });

        router.push({
          path: currentRoute.path,
          query: currentRoute.query,
          hash: currentRoute.hash,
        });

        // Set menus
        globals.$store.commit("storeMenus", response?.data?.permissions || []);

        // Set API routes
        const newItem: Record<string, string>[] = [];
        response?.data?.permissions.forEach((element: any) => {
          const dynamicApi = createDynamicApi(
            setCurrentPath(element?.children) || []
          );
          newItem.push(dynamicApi);
        });

        const customUrl = {
          viewIsExistCoupon: "/admin/promotion/coupon/check-is-exist",
          viewStockHistory: "/admin/stock-history/list",
          viewOverallRating: "/admin/product-review/rating-overall",
          updateStockAdjustment: "/admin/stock/adjustment",
          viewVariant: "/admin/variant/list",
          createVariant: "/admin/variant/create",
          detailVariant: "/admin/variant/detail/:id",
          updateVariant: "/admin/variant/update/:id",
          deleteVariant: "/admin/variant/delete",
          viewSaleOrderHistory: "/sale-order-history/list",
          updateSaleOrderBankPaymentStatus:
            "/admin/sale-order/bank-payment/update-sale-order-status",
          updateSaleOrderCashPaymentStatus:
            "/admin/sale-order/cash-on-delivery/update-sale-order-status",
        };

        const newArray = newItem.reduce((acc, obj) => {
          return { ...acc, ...obj };
        }, {});

        globals.$store.commit(
          "storeApiUrl",
          { ...newArray, ...customUrl } || []
        );

        // Get other services
        getMediaFolder();
      } catch (error: any) {
        store.commit("storeProcessLoad", false);
      }
    };

    const extractPermissions = (apiResponse: any[]): PermissionCode[] => {
      const permissions: PermissionCode[] = [];

      apiResponse.forEach((section) => {
        if (section.children) {
          section.children.forEach((subSection: { children: any[] }) => {
            if (subSection.children) {
              subSection.children.forEach((permission) => {
                if (permission.code) {
                  permissions.push(permission.code);
                }
              });
            }
          });
        }
      });

      return permissions;
    };

    const getMasterData = async (): Promise<void> => {
      await listMasterDataService().then(async (response) => {
        if (response?.expired) {
          const refresh = await regenerateTokenService();
          if (refresh) {
            return await getMasterData();
          }
        }

        globals.$store.commit("storeMasterData", response?.data || []);
      });
    };

    const getMediaFolder = async (): Promise<void> => {
      const params = queryParams({});

      await getMediaFolderService(params).then(async (response) => {
        if (response?.expired) {
          const refresh = await regenerateTokenService();
          if (refresh) {
            return await getMediaFolder();
          }
        }

        globals.$store.commit("storeMedia", response?.data || []);
      });
    };

    // event listeners
    const handleModalMouseOver = (event: MouseEvent): MouseEvent => {
      const modalContainer = document.querySelector(".modal-container");
      const body = document.querySelector("body");

      if (modalContainer && body) {
        body.classList.add("modal-active");
      } else if (body) {
        body.classList.remove("modal-active");
      }

      removeAndCopyClass();
      return event;
    };

    const removeAndCopyClass = (): void => {
      const nestedWrapper = document.querySelector(
        ".modal-container .modal-container"
      );
      const outsideDiv = document.querySelector(".outside-div");

      if (nestedWrapper && outsideDiv) {
        outsideDiv.appendChild(nestedWrapper);
      }
    };

    const handleClick = (e: MouseEvent): void => {
      console.log(e);
    };

    const overlayClick = (e: MouseEvent): void => {
      console.log(e);
    };

    const handleKeyPress = (event: KeyboardEvent | MouseEvent): void => {
      if (event instanceof KeyboardEvent) {
        if (event.ctrlKey && event.altKey && event.keyCode === 220) {
          event.preventDefault();
          isLocalSetting = !isLocalSetting;
        }

        if (
          event.ctrlKey &&
          event.altKey &&
          (event.key === "." || event.key === ">")
        ) {
          event.preventDefault();
          isCalculateExpression = !isCalculateExpression;
        }

        const photoPopup = document.querySelectorAll(
          ".PhotoSlider__FooterWrap"
        );
        const mediaPopup = document.querySelectorAll(".media-popup");
        const modalExpression = document.querySelectorAll(".modal-expression");

        if (photoPopup.length || mediaPopup.length || modalExpression.length) {
          return;
        }

        const modalContainers = document.querySelectorAll(".modal-container");
        const hasEscapeConfirmClass = Array.from(modalContainers).some(
          (container: Element) => container.querySelector(".escape-confirm")
        );

        if (hasEscapeConfirmClass && event.keyCode === 27) {
          idEscape = true;
          newModalContainers = modalContainers;
        } else {
          modalContainers.forEach((container: Element) => {
            enableClick(container, event);
          });
        }
      }
    };

    const enableClick = (
      modalContainer: Element,
      event: KeyboardEvent | MouseEvent
    ): void => {
      const overlay = modalContainer.querySelector(".overlay");

      if (overlay) {
        const computedStyle = window.getComputedStyle(overlay);
        const zIndex = parseInt(
          computedStyle.getPropertyValue("z-index") || "0"
        );

        const minZIndex = 100;

        if (
          zIndex >= minZIndex &&
          event instanceof KeyboardEvent &&
          event.keyCode === 27
        ) {
          (overlay as HTMLElement).click();
          idEscape = false;
          newModalContainers = document.querySelectorAll(".modal-container");
        } else if (event instanceof MouseEvent) {
          (overlay as HTMLElement).click();
          idEscape = false;
          newModalContainers = document.querySelectorAll(".modal-container");
        }
      }
    };

    const applicationMode =
      localStorage.getItem("applicationMode") || "light-mode";

    // return handlers && listeners
    return {
      isLoading,
      isLocalSetting,
      isCalculateExpression,
      idEscape,
      newModalContainers,
      serverError,
      handleClick,
      overlayClick,
      applicationMode,
    };
  },
});
